import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChartBar, SignOut, FileText, Users, Briefcase, Plus, Download, Stack } from '@phosphor-icons/react';
import { collection, query, where, getDocs, orderBy, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { toast } from 'sonner';
import {useFirebaseAuth} from "../context/FirebaseAuthContext";
import {db} from "../lib/firebase";
import {format} from "date-fns";
import {Button} from "../components/ui/button";
import {Card} from "../components/ui/card";
import WorkExperienceModal from "../components/WorkExperienceModal";
import AssessmentDetailModal from "../components/AssessmentDetailModal";
import SEO from "../seo/SEO";

const CandidateDashboard = () => {
    const { user, userData, signOut, loading: authLoading } = useFirebaseAuth();
    const navigate = useNavigate();
    const [workExperiences, setWorkExperiences] = useState([]);
    const [assessments, setAssessments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showWorkExpModal, setShowWorkExpModal] = useState(false);
    const [selectedAssessment, setSelectedAssessment] = useState(null);
    const [editingExperience, setEditingExperience] = useState(null);

    useEffect(() => {
        // Redirect if user is not a candidate
        if (!authLoading && userData && userData.role !== 'candidate') {
            const redirectPath = userData.role === 'mentor' ? '/mentor' : '/admin';
            navigate(redirectPath);
        }
    }, [userData, authLoading, navigate]);

    useEffect(() => {
        if (user && userData?.role === 'candidate') {
            fetchData();
        }
    }, [user, userData]);

    const fetchData = async () => {
        if (!user) return;

        setLoading(true);
        try {
            // Fetch work experiences from Firestore
            const workExpRef = collection(db, 'workExperiences');
            const workExpQuery = query(
                workExpRef,
                where('userId', '==', user.uid),
                orderBy('created_at', 'desc')
            );
            const workExpSnapshot = await getDocs(workExpQuery);
            const workExpList = workExpSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                created_at: doc.data().created_at?.toDate?.() || new Date(doc.data().created_at)
            }));
            setWorkExperiences(workExpList);

            // Fetch assessments from Firestore
            const assessmentsRef = collection(db, 'assessments');
            const assessmentsQuery = query(
                assessmentsRef,
                where('userId', '==', user.uid),
                orderBy('created_at', 'desc')
            );
            const assessmentsSnapshot = await getDocs(assessmentsQuery);
            const assessmentsList = assessmentsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                created_at: doc.data().created_at?.toDate?.() || new Date(doc.data().created_at)
            }));
            setAssessments(assessmentsList);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load your data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            await signOut();
            navigate('/');
            toast.success('Logged out successfully');
        } catch (error) {
            console.error('Error logging out:', error);
            toast.error('Failed to log out');
        }
    };

    const handleDeleteExperience = async (expId) => {
        if (window.confirm('Are you sure you want to delete this work experience? This will also delete all associated assessments.')) {
            try {
                // Delete work experience
                const workExpRef = doc(db, 'workExperiences', expId);
                await deleteDoc(workExpRef);

                // Delete associated assessments
                const assessmentsRef = collection(db, 'assessments');
                const assessmentsQuery = query(assessmentsRef, where('workExpId', '==', expId));
                const assessmentsSnapshot = await getDocs(assessmentsQuery);

                const deletePromises = assessmentsSnapshot.docs.map(doc => deleteDoc(doc.ref));
                await Promise.all(deletePromises);

                toast.success('Work experience deleted successfully');
                fetchData(); // Refresh data
            } catch (error) {
                console.error('Error deleting experience:', error);
                toast.error('Failed to delete work experience');
            }
        }
    };

    const getScoreColor = (score) => {
        if (score >= 80) return 'text-[#009944]';
        if (score >= 60) return 'text-[#FFB800]';
        if (score >= 40) return 'text-[#FF5A00]';
        return 'text-[#DC2626]';
    };

    const getReadinessColor = (readinessLevel) => {
        switch(readinessLevel) {
            case 'Ready for Registration':
                return 'bg-[#009944]/10 text-[#009944]';
            case 'Close to Ready':
                return 'bg-[#FFB800]/10 text-[#FFB800]';
            case 'Needs Improvement':
                return 'bg-[#FF5A00]/10 text-[#FF5A00]';
            default:
                return 'bg-[#666666]/10 text-[#666666]';
        }
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        try {
            return format(date, 'PPP');
        } catch {
            return 'Invalid date';
        }
    };

    const generateUuid = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    };

    const latestAssessment = assessments[0];

    if (authLoading || loading) {
        return (
            <div className="min-h-screen bg-[#F9F9F6] flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-[#002FA7] border-t-transparent rounded-full animate-spin mx-auto"></div>
                    <p className="mt-4 text-[#666666]">Loading your dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F9F9F6]">
            <SEO title="Dashboard | MyProfessionalPath"
                 description="Track your professional development and competence which makes your professiona registration easy."
                 canonicalURL="https://myprofessionalpath.web.app" />
            {/* Navigation */}
            <nav className="sticky-nav bg-white border-b border-[#DCDCDA]" data-testid="candidate-nav">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center space-x-3">
                            <ChartBar size={32} weight="duotone" className="text-[#002FA7]" />
                            <span className="font-heading text-xl font-semibold text-[#0A0A0A]">MyProfessionalPath</span>
                        </div>
                        <div className="flex items-center space-x-4">
                            <Button
                                variant="ghost"
                                onClick={() => navigate('/dashboard')}
                                data-testid="nav-dashboard-btn"
                                className="text-sm font-medium"
                            >
                                Dashboard
                            </Button>
                            <Button
                                variant="ghost"
                                onClick={() => navigate('/mentors')}
                                data-testid="nav-mentors-btn"
                                className="text-sm font-medium"
                            >
                                Find Mentors
                            </Button>
                            <Button
                                variant="ghost"
                                onClick={handleLogout}
                                data-testid="nav-logout-btn"
                                className="text-sm font-medium flex items-center gap-2"
                            >
                                <SignOut size={18} />
                                Sign Out
                            </Button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                {/* Header */}
                <div className="mb-12">
                    <h1 className="font-heading text-4xl font-semibold text-[#0A0A0A] mb-2">
                        Welcome back, {userData?.full_name || user?.displayName || 'Engineer'}
                    </h1>
                    <p className="text-base text-[#666666]">
                        {userData?.discipline || 'Discipline not specified'} •
                        {userData?.years_of_experience ? ` ${userData.years_of_experience} years experience` : ' Experience not specified'}
                    </p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-testid="stats-grid">
                    <Card className="swiss-card p-6">
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#666666] mb-1">Total Assessments</p>
                                <p className="font-heading text-3xl font-semibold text-[#0A0A0A]">{assessments.length}</p>
                            </div>
                            <div className="w-12 h-12 bg-[#002FA7]/10 rounded-sm flex items-center justify-center">
                                <ChartBar size={24} weight="duotone" className="text-[#002FA7]" />
                            </div>
                        </div>
                        {latestAssessment && (
                            <p className="text-sm text-[#666666]">
                                Latest: {formatDate(latestAssessment.created_at)}
                            </p>
                        )}
                    </Card>

                    <Card className="swiss-card p-6">
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#666666] mb-1">Work Experience</p>
                                <p className="font-heading text-3xl font-semibold text-[#0A0A0A]">{workExperiences.length}</p>
                            </div>
                            <div className="w-12 h-12 bg-[#009944]/10 rounded-sm flex items-center justify-center">
                                <Briefcase size={24} weight="duotone" className="text-[#009944]" />
                            </div>
                        </div>
                        <p className="text-sm text-[#666666]">
                            {workExperiences.length === 1 ? 'Entry submitted' : 'Entries submitted'}
                        </p>
                    </Card>

                    <Card className="swiss-card p-6">
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#666666] mb-1">Latest Score</p>
                                <p className={`font-heading text-3xl font-semibold ${latestAssessment ? getScoreColor(latestAssessment.overall_score) : 'text-[#666666]'}`}>
                                    {latestAssessment ? `${latestAssessment.overall_score}/100` : 'N/A'}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-[#FF5A00]/10 rounded-sm flex items-center justify-center">
                                <FileText size={24} weight="duotone" className="text-[#FF5A00]" />
                            </div>
                        </div>
                        {latestAssessment && (
                            <p className={`text-sm font-medium px-2 py-1 rounded-sm inline-block ${getReadinessColor(latestAssessment.readiness_level)}`}>
                                {latestAssessment.readiness_level}
                            </p>
                        )}
                    </Card>
                </div>

                {/* Consolidated Assessment CTA - shown when user has 2+ work experiences */}
                {workExperiences.length >= 2 && (
                    <Card
                        className="p-8 mb-12 border rounded-sm"
                        style={{ background: '#002FA7', borderColor: '#002FA7' }}
                        data-testid="consolidated-cta-card"
                    >
                        <div className="flex items-start justify-between gap-6 flex-wrap">
                            <div className="flex items-start gap-4 flex-1 min-w-[280px]">
                                <div className="w-14 h-14 bg-white/10 rounded-sm flex items-center justify-center flex-shrink-0">
                                    <Stack size={28} weight="duotone" className="text-white" />
                                </div>
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/70 mb-1">NEW</p>
                                    <h3 className="font-heading text-xl font-semibold text-white mb-2">
                                        Consolidated Readiness Assessment
                                    </h3>
                                    <p className="text-sm text-white/90 leading-relaxed max-w-2xl">
                                        Get one overall readiness verdict by analysing ALL {workExperiences.length} of your work experiences together. The report includes every submission and tells you which ECSA outcomes need more evidence.
                                    </p>
                                </div>
                            </div>
                            <Button
                                onClick={() => navigate(`/assess/consolidated/${generateUuid()}`)}
                                data-testid="run-consolidated-btn"
                                className="bg-white text-[#002FA7] hover:bg-[#F9F9F6] rounded-sm font-medium px-6 py-6"
                            >
                                Run Consolidated Assessment
                            </Button>
                        </div>
                    </Card>
                )}

                {/* Work Experience Section */}
                <div className="mb-12">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="font-heading text-2xl font-semibold text-[#0A0A0A]">Work Experience</h2>
                        <Button
                            onClick={() => {
                                setEditingExperience(null);
                                setShowWorkExpModal(true);
                            }}
                            data-testid="add-work-experience-btn"
                            className="bg-[#002FA7] hover:bg-[#0040D9] text-white rounded-sm flex items-center gap-2"
                        >
                            <Plus size={18} weight="bold" />
                            Add Experience
                        </Button>
                    </div>

                    {workExperiences.length === 0 ? (
                        <Card className="swiss-card p-12 text-center">
                            <FileText size={48} weight="duotone" className="text-[#666666] mx-auto mb-4" />
                            <p className="text-base text-[#666666] mb-4">No work experience submitted yet</p>
                            <Button
                                onClick={() => {
                                    setEditingExperience(null);
                                    setShowWorkExpModal(true);
                                }}
                                data-testid="add-first-experience-btn"
                                className="bg-[#002FA7] hover:bg-[#0040D9] text-white rounded-sm"
                            >
                                Add Your First Experience
                            </Button>
                        </Card>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {workExperiences.map((exp) => (
                                <Card key={exp.id} className="swiss-card p-6 hover:shadow-lg transition-shadow" data-testid={`work-exp-${exp.id}`}>
                                    <div className="flex items-start justify-between mb-3">
                                        <h3 className="font-heading text-lg font-medium text-[#0A0A0A]">{exp.title}</h3>
                                        <div className="flex gap-2">
                                            {exp.original_filename && (
                                                <span className="bg-[#002FA7]/10 text-[#002FA7] px-2 py-1 rounded-sm text-xs">
                          PDF
                        </span>
                                            )}
                                        </div>
                                    </div>
                                    <p className="text-sm text-[#666666] mb-4 line-clamp-3">{exp.description}</p>
                                    <div className="flex items-center justify-between">
                                        <p className="text-xs text-[#666666]">{formatDate(exp.created_at)}</p>
                                        <div className="flex gap-2">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => navigate(`/assess/${exp.id}`)}
                                                data-testid={`assess-btn-${exp.id}`}
                                                className="border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5 rounded-sm"
                                            >
                                                Start Assessment
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleDeleteExperience(exp.id)}
                                                className="border-red-500 text-red-500 hover:bg-red-50 rounded-sm"
                                            >
                                                Delete
                                            </Button>
                                        </div>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>

                {/* Assessment History */}
                <div>
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="font-heading text-2xl font-semibold text-[#0A0A0A]">Assessment History</h2>
                    </div>

                    {assessments.length === 0 ? (
                        <Card className="swiss-card p-12 text-center">
                            <ChartBar size={48} weight="duotone" className="text-[#666666] mx-auto mb-4" />
                            <p className="text-base text-[#666666]">No assessments yet. Start by submitting your work experience.</p>
                        </Card>
                    ) : (
                        <div className="space-y-4">
                            {assessments.map((assessment) => (
                                <Card key={assessment.id} className="swiss-card p-6 hover:shadow-lg transition-shadow" data-testid={`assessment-${assessment.id}`}>
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <span className={`font-heading text-2xl font-semibold ${getScoreColor(assessment.overall_score)}`}>
                          {assessment.overall_score}/100
                        </span>
                                                <span className={`text-sm font-medium px-3 py-1 rounded-sm ${getReadinessColor(assessment.readiness_level)}`}>
                          {assessment.readiness_level}
                        </span>
                                                <span className="bg-[#666666]/10 text-[#666666] px-2 py-1 rounded-sm text-xs">
                          {assessment.model_used || 'AI Model'}
                        </span>
                                                {assessment.is_consolidated && (
                                                    <span className="bg-[#FF5A00]/10 text-[#FF5A00] border-[#FF5A00]/30 px-2 py-1 rounded-sm text-xs" data-testid={`consolidated-badge-${assessment.id}`}>
                            CONSOLIDATED
                          </span>
                                                )}
                                            </div>
                                            <p className="text-sm text-[#666666] mb-3">{formatDate(assessment.created_at)}</p>
                                            {assessment.workExpTitle && !assessment.is_consolidated && (
                                                <p className="text-xs text-[#666666] mb-2">
                                                    Based on: {assessment.workExpTitle}
                                                </p>
                                            )}
                                            <div className="flex gap-2">
                                                <Button
                                                    size="sm"
                                                    onClick={() => setSelectedAssessment(assessment)}
                                                    data-testid={`view-details-btn-${assessment.id}`}
                                                    className="bg-[#002FA7] hover:bg-[#0040D9] text-white rounded-sm"
                                                >
                                                    View Details
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => navigate(`/export/${assessment.id}`)}
                                                    data-testid={`export-btn-${assessment.id}`}
                                                    className="border-[#FF5A00] text-[#FF5A00] hover:bg-[#FF5A00]/5 rounded-sm flex items-center gap-2"
                                                >
                                                    <Download size={16} />
                                                    Export
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
            </main>

            {/* Modals */}
            {showWorkExpModal && (
                <WorkExperienceModal
                    editMode={!!editingExperience}
                    experienceData={editingExperience}
                    onClose={() => {
                        setShowWorkExpModal(false);
                        setEditingExperience(null);
                    }}
                    onSuccess={() => {
                        setShowWorkExpModal(false);
                        setEditingExperience(null);
                        fetchData();
                    }}
                />
            )}

            {selectedAssessment && (
                <AssessmentDetailModal
                    assessment={selectedAssessment}
                    onClose={() => setSelectedAssessment(null)}
                />
            )}
        </div>
    );
};

export default CandidateDashboard;